import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  SxProps,
  useMediaQuery,
  Backdrop,
} from '@mui/material';
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import SearchDropDown from '../../../common/SearchDropDown';
import ErrorBadge from '../../../common/ErrorBadge';
import { NavigationGroup } from '../types';
import { PropertyField } from '../../../../system/field';
import SetFieldDefaults from '../SetFieldDefaults';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { EditNavigationIcons } from '../EditNavigationIcons';
import {
  PreferenceKey,
  usePreferences,
} from '../../../../hooks/usePreferences';

interface DesktopNavigationProps {
  groups: NavigationGroup[];
  activeGroup: string;
  activeSection: string;
  navHeight: string;
  sectionErrors: { [key: string]: number };
  groupErrors: { [key: string]: number };
  setDefaultsStyles: SxProps;
  handleSearchEntriesChange: (fields: PropertyField[] | null) => void;
  handleGroupClick: (
    groupName: string,
    fieldName?: string,
    isMobile?: boolean,
    followError?: boolean,
  ) => void;
  handleSectionClick: (sectionName: string, followError?: boolean) => void;
}

const ResponsiveDesktopNavigation: React.FC<DesktopNavigationProps> = ({
  groups,
  activeGroup,
  activeSection,
  navHeight,
  sectionErrors,
  groupErrors,
  setDefaultsStyles,
  handleGroupClick,
  handleSectionClick,
  handleSearchEntriesChange,
}) => {
  const { hasFeature } = useFeatureFlags();
  const hasSetFieldDefaults = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);
  const { usePreference } = usePreferences();
  const [backdropOpen, setBackdropOpen] = useState(false);
  // Use the preferences hook for AI descriptions enabled state
  const [menuIsExpanded, setMenuIsExpanded] = usePreference<boolean>(
    PreferenceKey.EDIT_MENU_IS_EXPANDED,
    true, // default value: enabled
  );
  const isMediumScreenSize = useMediaQuery((theme) =>
    // This could be changed to small screen sizes
    theme.breakpoints.down('md'),
  );
  const menuOpenedWidth = 340;
  const menuClosedWidth = 64;

  const badgeSxProps: SxProps = {
    position: 'absolute',
    right: '8px',
  };

  const handleClose = useCallback(() => {
    setBackdropOpen(false);
    setMenuIsExpanded(false);
  }, []);

  const getSidebarSxProps = useCallback(() => {
    // For medium and small screens
    if (isMediumScreenSize) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: menuIsExpanded ? menuOpenedWidth : menuClosedWidth,
        maxWidth: menuOpenedWidth,
        boxShadow: '5px 0 15px rgba(0,0,0,0.3)',
      };
    }

    return {
      width: menuIsExpanded ? menuOpenedWidth : menuClosedWidth,
    };
  }, [isMediumScreenSize, menuIsExpanded]);

  const getSidebarWrapperSxProps = useCallback(() => {
    // For medium and small screens
    if (isMediumScreenSize) {
      return {
        width: menuClosedWidth,
      };
    }

    return {
      width: menuIsExpanded ? 1 : menuClosedWidth,
      maxWidth: menuOpenedWidth,
    };
  }, [isMediumScreenSize, menuIsExpanded]);

  useEffect(() => {
    if (isMediumScreenSize && menuIsExpanded) {
      setBackdropOpen(true);
    } else {
      setBackdropOpen(false);
    }
  }, [isMediumScreenSize, menuIsExpanded, setBackdropOpen]);

  return (
    <Box
      // Sidebar Wrapper
      data-testid="edit-navigation"
      sx={{
        bgcolor: 'background.paper',
        border: 'none',
        height: 'calc(100vh - var(--active-header-height, 0px))',
        position: 'sticky',
        top: 'var(--active-header-height, 125px)',
        transition: `width 0.1s ease`,
        zIndex: 1500,
        ...getSidebarWrapperSxProps(),
      }}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: 1 }}
        open={backdropOpen}
        onClick={handleClose}
      />
      <Box
        // Sidebar container
        sx={{
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'grey.300',
          display: 'flex',
          flexDirection: 'column',
          height: 1,
          overflow: 'hidden',
          transition: 'width 0.1s ease',
          zIndex: 10,
          ...getSidebarSxProps(),
        }}
      >
        <Box>
          <Box
            sx={{
              px: 2,
              py: 3,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {menuIsExpanded ? (
              <KeyboardDoubleArrowLeft
                fill="secondary.medium"
                onClick={() => setMenuIsExpanded(false)}
              />
            ) : (
              <KeyboardDoubleArrowRight
                fill="secondary.medium"
                onClick={() => setMenuIsExpanded(true)}
              />
            )}
            <Typography
              sx={{
                color: 'secondary.medium',
                fontSize: '20px',
                fontWeight: '700',
                lineHeight: '30px',
                marginLeft: '10px',
                opacity: menuIsExpanded ? 1 : 0,
                transition: 'opacity 0.1s',
                whiteSpace: 'nowrap',
              }}
            >
              Listing Sections
            </Typography>
          </Box>
          <Box
            sx={{
              mb: 1,
            }}
            onClick={() => {
              if (!menuIsExpanded) {
                setMenuIsExpanded(true);
              }
            }}
          >
            <SearchDropDown
              menuIsExpanded={menuIsExpanded}
              data={groups}
              onResultsChange={handleSearchEntriesChange}
              onClick={(groupName, fieldLabel) => {
                handleGroupClick(groupName, fieldLabel, false, false);
              }}
            />
          </Box>
          <Divider
            sx={{
              marginTop: '25px',
              borderWidth: '1px',
              width: '80%',
              margin: '0 auto',
            }}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            overflowX: 'hidden',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
          }}
          data-testid="body-content"
          data-nav-sections
        >
          <List data-testid="desktop-nav" component="nav">
            {groups.map((group) => (
              <React.Fragment key={group.name}>
                <ListItemButton
                  data-group-id={group.name}
                  selected={activeGroup === group.name}
                  onClick={() =>
                    handleGroupClick(group.name, undefined, false, false)
                  }
                  sx={{
                    color: 'secondary.medium',
                    marginTop: '11px',
                    lineHeight: '24px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    '&:hover': {
                      backgroundColor: 'background.secondary',
                    },
                    '&.Mui-selected.MuiListItemButton-root': {
                      backgroundColor: menuIsExpanded
                        ? 'transparent'
                        : 'background.secondary',
                    },
                  }}
                >
                  <EditNavigationIcons groupName={group.name} />
                  <ListItemText
                    primary={group.name}
                    id="desktop-nav-group-name"
                    sx={{
                      color: 'secondary.medium',
                      display: 'block',
                      fontSize: '0.9rem !important',
                      fontWeight: '400',
                      lineHeight: '20px',
                      pl: 1,
                      '& span': {
                        fontSize: '0.9rem !important',
                        opacity: menuIsExpanded ? 1 : 0,
                        transition: 'opacity 0.1s',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />

                  {groupErrors[group.name] > 0 &&
                    (activeGroup !== group.name || menuIsExpanded !== true) && (
                      <ErrorBadge
                        collapsed={menuIsExpanded === false}
                        count={groupErrors[group.name]}
                        onClick={() =>
                          handleGroupClick(group.name, undefined, false, true)
                        }
                        sx={badgeSxProps}
                      />
                    )}
                </ListItemButton>
                {activeGroup === group.name && (
                  <Box
                    sx={{
                      marginTop: menuIsExpanded ? '0px' : '8px',
                      marginRight: '16px',
                    }}
                  >
                    {group.sections.map((section) => (
                      <Box
                        key={section.name}
                        data-section-id={section.name}
                        onClick={() => handleSectionClick(section.name, false)}
                        sx={{
                          display: menuIsExpanded ? 'flex' : 'none',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          scrollMargin: '20px',
                        }}
                      >
                        <Typography
                          sx={{
                            alignItems: 'center',
                            backgroundColor:
                              activeSection === section.name
                                ? 'background.secondary'
                                : 'transparent',
                            borderBottomRightRadius: '8px',
                            borderTopRightRadius: '8px',
                            color: 'secondary.medium',
                            display: 'flex',
                            flexGrow: 1,
                            fontSize: '14px',
                            fontWeight: '400',
                            justifyContent: 'flex-start',
                            marginRight: '16px',
                            minHeight: '40px',
                            paddingLeft: '40px',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: 'background.secondary',
                            },
                          }}
                        >
                          {section.name}
                        </Typography>
                        {sectionErrors[section.name] > 0 && (
                          <ErrorBadge
                            collapsed={menuIsExpanded === false}
                            count={sectionErrors[section.name]}
                            onClick={() =>
                              handleSectionClick(section.name, true)
                            }
                            sx={badgeSxProps}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
        {hasSetFieldDefaults && (
          <Box
            sx={{
              '& .MuiBox-root': {
                background: 'white',
                position: 'relative',
                mt: 0,
                maxWidth: `${(menuIsExpanded ? menuOpenedWidth : menuClosedWidth) - 1}px`,
                width: 1,
              },
            }}
          >
            <SetFieldDefaults
              outlinedIcon={true}
              content={
                <Typography
                  sx={{
                    opacity: menuIsExpanded ? 1 : 0,
                    transition: 'all 0.1s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Set Field Defaults
                </Typography>
              }
              isMobile={false}
              styles={{
                ...setDefaultsStyles,
                position: 'relative',
                minWidth: `${menuClosedWidth - 1}px`,
                transition: 'all 0.1s',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResponsiveDesktopNavigation;
